/**
 * `tracking_substate` → the words and the colour on an active-order card.
 *
 * The classification itself is the server's job (`crates/orders/src/tracking.rs` — 12 codes,
 * including the «поиск идёт дольше обычного» threshold). The client's only job is to translate,
 * and the rules that follow are the ones both mobile clients had to learn twice:
 *
 * - **Never show `state`.** The active-orders response carries both `state` (one of ~38 raw
 *   SCREAMING_SNAKE `OrderState` values) and `tracking_substate`. Only the latter is meant for a
 *   person; rendering the former is how a card once ended up with a 40-character label that
 *   measured wider than the screen and looked empty.
 * - **An unknown code must render, not crash and not vanish.** A server that adds a 13th substate
 *   must not blank out the card of somebody with a tanker en route. Unknown codes come through as
 *   themselves, marked `raw`; ширину им, как и знакомым меткам, ограничивает сам `Pill`.
 * - **Colour carries information.** Android varies the pill variant per status (amber for «ищем
 *   дольше обычного», blue for «в пути»); iOS pins every pill to `.info` and loses that. The
 *   Android table is the one reproduced below — colour is never the ONLY signal, the pill always
 *   carries a word too.
 */

/** Mirrors the `Pill` variants; declared here so this module stays view-independent. */
export type StatusVariant = 'success' | 'warning' | 'info' | 'danger' | 'neutral';

export interface OrderStatusUi {
  /** Pill text — «В пути», «Ищем оператора», or the raw code when we do not know it. */
  label: string;
  variant: StatusVariant;
  /** The progress phrase under the car headline. Empty for terminal states, which say it all. */
  detail: string;
  /**
   * `label` is the server's own string — do not trust it to be short.
   *
   * Ширину больше не ограничивает вид: это делает сам `Pill`, и делает для ЛЮБОЙ метки. Раньше
   * потолок стоял только здесь, и знакомое «Ищем оператора» съедало карточку не хуже чужого кода.
   */
  raw: boolean;
}

/** The 12 substates, verbatim from Android's `OrderStatusUi` table. */
const KNOWN: Record<string, Omit<OrderStatusUi, 'raw'>> = {
  SEARCHING: { label: 'Ищем оператора', variant: 'neutral', detail: 'подбираем оператора' },
  SEARCHING_SLOW: {
    label: 'Ищем оператора',
    variant: 'warning',
    detail: 'поиск занимает больше обычного',
  },
  REASSIGNING: {
    label: 'Ищем оператора',
    variant: 'warning',
    detail: 'оператор сменился — ищем нового',
  },
  ASSIGNED: { label: 'Назначен', variant: 'info', detail: 'оператор назначен' },
  EN_ROUTE: { label: 'В пути', variant: 'info', detail: 'оператор едет к вам' },
  DISPATCHER_REVIEW_START: {
    label: 'В пути',
    variant: 'warning',
    detail: 'на проверке диспетчера',
  },
  ARRIVED: { label: 'На месте', variant: 'success', detail: 'оператор на месте' },
  REFILLING: { label: 'Заливает', variant: 'success', detail: 'идёт заправка' },
  DISPATCHER_REVIEW_COMPLETION: {
    label: 'Заливает',
    variant: 'warning',
    detail: 'на проверке диспетчера',
  },
  COMPLETING: { label: 'Завершение', variant: 'info', detail: 'обрабатывается' },
  COMPLETED: { label: 'Выполнен', variant: 'success', detail: '' },
  CANCELED: { label: 'Отменён', variant: 'neutral', detail: '' },
};

/**
 * What a missing substate becomes. An empty pill is a rendering bug, not a status, and the field
 * is non-nullable on the wire — so if it ever arrives blank, something upstream is broken and the
 * card should still say the true minimum: this выезд exists and is running.
 */
const BLANK: OrderStatusUi = { label: 'В работе', variant: 'neutral', detail: '', raw: false };

export function orderStatusUi(code: string): OrderStatusUi {
  const trimmed = code.trim();
  if (trimmed.length === 0) return BLANK;

  // Lookup is case-sensitive on purpose: the wire format is SCREAMING_SNAKE, and quietly
  // accepting `en_route` would hide a real contract change behind a working screen.
  const known = KNOWN[trimmed];
  if (known) return { ...known, raw: false };

  return { label: trimmed, variant: 'neutral', detail: '', raw: true };
}

/** Fields the headline needs — a subset, so tests do not have to build a whole order. */
export interface HeadlineSource {
  car_brand: string;
  car_model: string;
  car_count: number;
}

/**
 * «единицу / единицы / единиц техники».
 *
 * Bureaucratic, and deliberately: this is the fallback for an order whose vehicles came back
 * nameless, so we do not know whether they are cars. «Заказ на 3 машины» would be a guess, and
 * this app fuels boats and motorcycles too.
 */
function unitsWord(count: number): string {
  const tail = count % 10;
  const teen = count % 100 >= 11 && count % 100 <= 14;
  if (!teen && tail === 1) return 'единицу техники';
  if (!teen && tail >= 2 && tail <= 4) return 'единицы техники';
  return 'единиц техники';
}

/**
 * «Toyota Camry» · «Toyota Camry +2» · «Заказ на 3 машины».
 *
 * `car_brand`/`car_model` are `String` on the wire but come from `first.map(…).unwrap_or_default()`,
 * so an order whose car rows are gone yields **empty strings** — and the naive concatenation then
 * produces a card titled «&nbsp;+2», which is what this function exists to prevent.
 *
 * `car_count` counts only cars still on the trip (`PENDING`/`REFUELED`), so «+2» shrinking to «+1»
 * between two polls is correct behaviour, not a glitch. It is also read defensively: a summary
 * that ever arrives without the field must degrade to a single car, not to `NaN`.
 */
export function activeOrderHeadline(order: HeadlineSource): string {
  const count = Number.isFinite(order.car_count) ? Math.max(1, Math.trunc(order.car_count)) : 1;
  const name = [order.car_brand, order.car_model]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ');

  if (name.length > 0) return count > 1 ? `${name} +${count - 1}` : name;
  return count > 1 ? `Заказ на ${count} ${unitsWord(count)}` : 'Заказ в работе';
}
